import _uniqueId from 'lodash/uniqueId'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import type {RootState} from 'store/actions'
import isMobileVersion from 'store/mobile'

import useCachedData from 'hooks/cached_data'

import type {Inputable} from 'components/input'
import Input from 'components/input'
import SearchFooter from 'plugin/deployment/search_footer'

import {getJobNames} from '../store/actions'
import cleanJobNameForSearch from '../store/job'
import {JobsList} from './section'

// Minimal number of chars typed to trigger a search.
const minNumCharTyped = 2

const sectionStyle: React.CSSProperties = {
  padding: '0 25px',
}
const headerStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
}
const titleStyle: React.CSSProperties = {
  fontFamily: config.titleFont ? config.titleFont : 'Lato, Helvetica',
  fontSize: isMobileVersion ? 16 : 22,
  fontWeight: 'bold',
  margin: '0 20px 0 0',
}
const noPStyle: React.CSSProperties = {
  flex: 1,
  margin: 0,
}
const inputStyle: React.CSSProperties = {
  color: colors.DEPARTEMENT_SUGGEST_COLOR,
}
const jobListStyle: React.CSSProperties = {
  marginTop: 18,
}
const errorStyle: React.CSSProperties = {
  margin: 0,
}

const SearchSection = (props: unknown, ref: React.Ref<Inputable>): React.ReactElement|null => {
  const [searchText, setSearchText] = useState('')
  const {t} = useTranslation()
  const {data: allJobs, loading} = useCachedData<readonly NamedJobWithRomeId[]>(
    ({app: {upskillingJobNames}}: RootState) => upskillingJobNames,
    getJobNames(),
  )
  const titleId = useMemo(_uniqueId, [])
  const descriptionId = useMemo(_uniqueId, [])
  const cleanSearchText = cleanJobNameForSearch(searchText)
  const jobs = useMemo(
    () => cleanSearchText && allJobs ?
      allJobs.filter(({name}) => name.includes(cleanSearchText)).slice(0, 10) : [],
    [cleanSearchText, allJobs],
  )
  const hasEnoughChars = cleanSearchText.length >= minNumCharTyped
  const hasDescription = !hasEnoughChars || !jobs.length
  const [minHeight, setMinHeight] = useState(235)
  const jobsDivRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!jobsDivRef.current) {
      return
    }
    const height = jobsDivRef.current.clientHeight
    setMinHeight(previousMinHeight => Math.max(previousMinHeight, height))
  }, [jobs])
  const jobsDivStyle = useMemo((): React.CSSProperties => ({marginTop: 18, minHeight}), [minHeight])
  if (loading || !allJobs) {
    return null
  }
  return <section style={sectionStyle}>
    <div style={headerStyle}>
      <h2 id={titleId} style={titleStyle}>{t('Chercher un métier')}</h2>
      <p style={noPStyle} className="no-focus"><Input
        value={searchText} onChange={setSearchText} style={inputStyle}
        placeholder={t("Commencez à saisir le nom d'un métier")}
        aria-labelledby={titleId} name="search"
        aria-describedby={hasDescription ? descriptionId : undefined}
        ref={ref} /></p>
    </div>
    <div style={jobsDivStyle} ref={jobsDivRef}>
      {hasEnoughChars ?
        jobs.length ?
          <>
            <JobsList id="search" isExpanded={false} jobs={jobs} style={jobListStyle} />
            <SearchFooter />
          </> :
          <p role="status" id={descriptionId} style={errorStyle}>
            {t('Aucun métier ne correspond à la recherche\u00A0: {{searchText}}', {searchText})}
          </p> :
        searchText ? <p role="status" id={descriptionId} style={errorStyle}>
          {t('Continuez de saisir le nom du métier pour activer la recherche')}
        </p> : <p role="status" id={descriptionId} style={errorStyle}>
          {t("Commencez à saisir le nom d'un métier pour activer la recherche")}
        </p>
      }
    </div>
  </section>
}

export type {Inputable} from 'components/input'

export default React.memo(React.forwardRef(SearchSection))
